import { request, upload } from './client'
import type { FolderTree, Knowledge, KnowledgeBase, Page } from './types'

export const listKnowledgeBases = () => request<{ data: KnowledgeBase[] }>('/knowledge-bases').then((r) => r.data ?? [])

export const getKnowledgeBase = (id: string) => request<{ data: KnowledgeBase }>(`/knowledge-bases/${encodeURIComponent(id)}`).then((r) => r.data)

/** 新建知识库：沿用一个现有库的模型、切分、索引设置（template 就是 GET /knowledge-bases/:id 的完整返回）。 */
const COPIED = ['type', 'embedding_model_id', 'summary_model_id', 'chunking_config', 'indexing_strategy', 'image_processing_config', 'vlm_config', 'extract_config', 'question_generation_config', 'auto_tag_config', 'storage_provider_config', 'wiki_config', 'asr_config'] as const
export function createKnowledgeBase(input: { name: string; description: string; wiki: boolean; graph: boolean }, template: Record<string, unknown>) {
  const body: Record<string, unknown> = { name: input.name, description: input.description }
  for (const k of COPIED) if (template[k] !== undefined && template[k] !== null) body[k] = template[k]
  const strategy = { ...((template.indexing_strategy as Record<string, unknown>) ?? {}), wiki_enabled: input.wiki, graph_enabled: input.graph }
  body.indexing_strategy = strategy
  return request<{ data: KnowledgeBase }>('/knowledge-bases', { body }).then((r) => r.data)
}
export const getKnowledgeBaseRaw = (id: string) => request<{ data: Record<string, unknown> }>(`/knowledge-bases/${encodeURIComponent(id)}`).then((r) => r.data)
export const updateKnowledgeBase = (id: string, name: string, description: string) =>
  request<{ data: KnowledgeBase }>(`/knowledge-bases/${encodeURIComponent(id)}`, { method: 'PUT', body: { name, description } }).then((r) => r.data)
export const deleteKnowledgeBase = (id: string) => request(`/knowledge-bases/${encodeURIComponent(id)}`, { method: 'DELETE' })

export interface KnowledgeQuery {
  page?: number
  pageSize?: number
  keyword?: string
  /** undefined = 所有文件夹；'' = 根目录；'a/b' = 这个文件夹。 */
  folderPath?: string
  recursive?: boolean
  fileType?: string
  parseStatus?: string
  tagIds?: string[]
  startTime?: string
  endTime?: string
}

export function listKnowledge(kbId: string, q: KnowledgeQuery = {}) {
  return request<Page<Knowledge>>(`/knowledge-bases/${encodeURIComponent(kbId)}/knowledge`, {
    query: {
      page: q.page ?? 1,
      page_size: q.pageSize ?? 40,
      keyword: q.keyword || undefined,
      folder_path: q.folderPath,
      folder_recursive: q.folderPath !== undefined && q.recursive ? true : undefined,
      file_type: q.fileType || undefined,
      parse_status: q.parseStatus || undefined,
      tag_ids: q.tagIds?.length ? q.tagIds : undefined,
      start_time: q.startTime,
      end_time: q.endTime,
    },
  })
}

export const getFolderTree = (kbId: string) => request<{ data: FolderTree }>(`/knowledge-bases/${encodeURIComponent(kbId)}/knowledge/folders`).then((r) => r.data)

export const getKnowledge = (id: string) => request<{ data: Knowledge }>(`/knowledge/${encodeURIComponent(id)}`).then((r) => r.data)
/** 解析失败的资料重新解析一次（后台任务）。 */
export const reparseKnowledge = (id: string) => request(`/knowledge/${encodeURIComponent(id)}/reparse`, { body: {} })

/** 原文件（Markdown、PDF、图片……），按类型 inline 返回。 */
export const previewKnowledge = (id: string, signal?: AbortSignal) =>
  request<Response>(`/knowledge/${encodeURIComponent(id)}/preview`, { raw: true, signal }).then((r) => r.blob())

/** 重名上传时服务端回 409，并带回已有的那一篇。 */
export interface UploadResult {
  knowledge: Knowledge
  duplicate: boolean
}

export async function uploadFile(kbId: string, file: File, opts: { folderPath?: string; onProgress?: (f: number) => void } = {}): Promise<UploadResult> {
  const form = new FormData()
  form.append('file', file)
  // fileName 带相对路径时，服务端把它放进对应文件夹
  form.append('fileName', opts.folderPath ? `${opts.folderPath.replace(/\/+$/, '')}/${file.name}` : file.name)
  form.append('channel', 'web')
  try {
    const res = await upload<{ data: Knowledge }>(`/knowledge-bases/${encodeURIComponent(kbId)}/knowledge/file`, form, opts.onProgress)
    return { knowledge: res.data, duplicate: false }
  } catch (e) {
    const err = e as { status?: number; data?: { data?: Knowledge; code?: string } }
    if (err.status === 409 && err.data?.data) return { knowledge: err.data.data, duplicate: true }
    throw e
  }
}

const PROVIDER = /^(resource|local|minio|cos|tos|s3|oss|ks3|obs):\/\/\S+$/i
const STORAGE = /^storage:\/\/[0-9A-Za-z_-]+\/(resource|local|minio|cos|tos|s3|oss|ks3|obs):\/\/\S+$/i
export const isProtectedFile = (url: string) => PROVIDER.test(url.trim()) || STORAGE.test(url.trim())

/** 受保护的文件（迁移来的附件、图片）：登录态取 /files，共享知识库里的走知识库的代理。 */
export function fetchProtectedFile(fileUrl: string, kbId?: string, signal?: AbortSignal): Promise<Blob> {
  const path = kbId ? `/knowledge-bases/${encodeURIComponent(kbId)}/files` : '/files'
  return request<Response>(path, { query: { file_path: fileUrl.trim() }, raw: true, signal }).then((r) => r.blob())
}

export interface Chunk {
  id: string
  content: string
  chunk_index: number
  chunk_type?: string
}

/** 解析后的分块（预览 Word、网页这类没法直接显示原文件的资料时用）。 */
export const listChunks = (knowledgeId: string, pageSize = 60) =>
  request<{ data: Chunk[]; total: number }>(`/chunks/${encodeURIComponent(knowledgeId)}`, { query: { page: 1, page_size: pageSize } })
